/**
 * HTTP კლიენტი.
 *
 * Access token ინახება **მხოლოდ მეხსიერებაში** (არა localStorage-ში) —
 * XSS-ის შემთხვევაში ტოკენი ვერ მოიპარება. გვერდის განახლებისას
 * ის ხელახლა მოიპოვება refresh cookie-ით (`bootstrapSession`).
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code = 'ERROR',
    public details?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

/** ავტორიზაციის დაკარგვისას გამოსაძახებელი callback (router-ის გადამისამართებისთვის) */
export function setUnauthenticatedHandler(fn: (() => void) | null) {
  onUnauthenticated = fn;
}

type Options = Omit<RequestInit, 'body'> & { body?: unknown };

async function parseError(res: Response): Promise<ApiError> {
  let payload: { error?: { message?: string; code?: string; details?: Record<string, string> } } = {};
  try {
    payload = await res.json();
  } catch {
    /* პასუხი JSON არ იყო */
  }
  return new ApiError(
    res.status,
    payload.error?.message ?? `მოთხოვნა ჩავარდა (${res.status})`,
    payload.error?.code ?? 'ERROR',
    payload.error?.details,
  );
}

/** ერთი პარალელური refresh — რამდენიმე 401 ერთდროულად რომ არ ცდილობდეს */
let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  refreshPromise ??= (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return false;
      const data = (await res.json()) as { accessToken: string };
      accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // მომდევნო 401-ს ახალი მცდელობა დასჭირდება
      setTimeout(() => {
        refreshPromise = null;
      }, 0);
    }
  })();
  return refreshPromise;
}

async function raw(path: string, options: Options, retry = true): Promise<Response> {
  const headers = new Headers(options.headers);
  if (options.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

  const res = await fetch(path.startsWith('/api') ? path : `/api${path}`, {
    ...options,
    headers,
    credentials: 'include',
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  // access token ამოიწურა → ერთხელ ვცდილობთ განახლებას
  if (res.status === 401 && retry && !path.includes('/auth/refresh')) {
    const ok = await tryRefresh();
    if (ok) return raw(path, options, false);
    accessToken = null;
    onUnauthenticated?.();
  }

  return res;
}

export async function api<T = unknown>(path: string, options: Options = {}): Promise<T> {
  const res = await raw(path, options);
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const http = {
  get: <T>(path: string) => api<T>(path),
  post: <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => api<T>(path, { method: 'DELETE' }),
};

/**
 * გვერდის ჩატვირთვისას სესიის აღდგენა refresh cookie-ით.
 * აბრუნებს მომხმარებელს ან null-ს.
 */
export async function bootstrapSession<T>(): Promise<T | null> {
  const ok = await tryRefresh();
  if (!ok) return null;
  try {
    return await http.get<T>('/auth/me');
  } catch {
    accessToken = null;
    return null;
  }
}
